import type { Address, Appointment, Patient } from '@medplum/fhirtypes';

export const ATTENDANCE_LOCATION_HOME = 'Home';

const LOC_TYPE = 'LOC';
const ROOM_TYPE = 'ROOM';

function participantHasType(participant: Appointment['participant'][0], code: string): boolean {
  return (
    participant.type?.some((t) =>
      t.coding?.some((c) => c.code?.toUpperCase() === code),
    ) ?? false
  );
}

export function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

export function formatAddressLabel(address: Address): string | undefined {
  const parts = [
    address.line?.join(', '),
    address.city,
    address.state,
    address.postalCode,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : undefined;
}

/** Practice site / room labels from appointment participants (excludes video URLs). */
export function buildPracticeLocationOptionsFromAppointment(appointment: Appointment): string[] {
  const out: string[] = [];
  for (const p of appointment.participant ?? []) {
    if (!participantHasType(p, LOC_TYPE) && !participantHasType(p, ROOM_TYPE)) continue;
    const label = p.actor?.display?.trim();
    if (label && !isHttpUrl(label)) out.push(label);
  }
  return [...new Set(out)];
}

/** Location picker options for one attendance row: client addresses, Home, practice sites. */
export function getPatientLocationOptions(
  patient: Patient | undefined,
  practiceLocations: string[] = [],
): string[] {
  const out: string[] = [];
  for (const addr of patient?.address ?? []) {
    const label = formatAddressLabel(addr);
    if (label && !out.includes(label)) out.push(label);
  }
  for (const site of practiceLocations) {
    if (site && !isHttpUrl(site) && !out.includes(site)) out.push(site);
  }
  if (!out.includes(ATTENDANCE_LOCATION_HOME)) out.push(ATTENDANCE_LOCATION_HOME);
  return out;
}

/** Non-URL locations saved on metadata snapshot (legacy centralized field). */
export function nonUrlLocationsFromMetadata(
  locations: string[] | undefined,
): string[] {
  return (locations ?? []).filter((l) => l.trim() && !isHttpUrl(l));
}

/**
 * Best default attendance location when status is Completed and the picker is empty.
 * Prefer confirmation → legacy metadata → practice site → patient address.
 * Never invent Home — signing must fail until the clinician picks a place of service.
 */
export function resolveDefaultParticipantLocation(params: {
  existing?: string | undefined;
  confirmationLabel?: string | undefined;
  practiceLocations?: string[];
  patient?: Patient | undefined;
  legacyMetadataLocations?: string[];
}): string | undefined {
  const trimmed = params.existing?.trim();
  if (trimmed) return trimmed;
  const confirmation = params.confirmationLabel?.trim();
  if (confirmation && !isHttpUrl(confirmation)) return confirmation;
  const legacy = nonUrlLocationsFromMetadata(params.legacyMetadataLocations);
  if (legacy[0]) return legacy[0];
  const practice = (params.practiceLocations ?? []).find((l) => l.trim() && !isHttpUrl(l));
  if (practice) return practice;
  const options = getPatientLocationOptions(params.patient, params.practiceLocations ?? []);
  return options.find((o) => o !== ATTENDANCE_LOCATION_HOME);
}

